/**
 * personnelHelpers — shared constants and pure functions used across the
 * personnel components (PostIcon, EquipmentModal, OfficerCard,
 * PersonnelHeader, PersonnelCard).
 *
 * Extracted verbatim from the legacy PersonnelCard.tsx so behaviour is
 * byte-identical.
 */
import { SUPERVISOR_BADGE, OFFICER_BADGE, FCPD_BADGE } from '../../assets'
import type { Post, SecondaryTask } from '../../stores/desktop'

/** First N personnel slots are supervisors (gold badges). */
export const SUPERVISOR_SPLIT = 3

/** Slot position determines badge color — gold for sups, silver for officers. */
export function getBadge(isSup: boolean, role?: string): string {
  if (role === 'FCPD') return FCPD_BADGE
  return isSup ? SUPERVISOR_BADGE : OFFICER_BADGE
}

/**
 * Short display rank for the badge overlay.
 * Covers both slot roles and roster categories.
 */
export const RANK_LABEL: Record<string, string> = {
  'Dir/AD/Lt':             'DIR / LT',
  'Sergeant':              'SERGEANT',
  'Sergeants':             'SERGEANT',
  'OIC':                   'OIC',
  'Officer':               'OFFICER',
  'Officers':              'OFFICER',
  'Dispatcher':            'DISPATCHER',
  'Dispatchers':           'DISPATCHER',
  'Developer':             'DEVELOPER',
  'Lieutenant':            'LIEUTENANT',
  'Director of Security':  'DIRECTOR',
  'Assistant Director':    'ASST. DIR',
  'Fairfax County Police': 'POLICE',
  'Management':            'MANAGEMENT',
  'Engineering':           'ENGINEERING',
}

/**
 * Friendly display role for the card header.
 * Maps roster categories to short, human-readable labels.
 */
export const DISPLAY_ROLE: Record<string, string> = {
  'Director of Security':  'Director',
  'Assistant Director':    'Asst. Director',
  'Lieutenant':            'Lieutenant',
  'Sergeants':             'Sergeant',
  'Officers':              'Officer',
  'Dispatchers':           'Dispatcher',
  'Fairfax County Police': 'Police',
  'Management':            'Management',
  'Engineering':           'Engineering',
  'Developer':             'Developer',
}

/** Strip "(nickname)" annotations from a name. */
export function stripNickname(name: string): string {
  return name.replace(/\s*\(.*?\)\s*/g, '').trim()
}

/** Extract surname (text before the comma) from a "Last, First" name. */
export function lastName(name: string): string {
  const s = stripNickname(name)
  return s.split(',')[0].trim()
}

/** Extract the numeric portion of a call sign (e.g. "S-01" → "01"). */
export function csNumber(callSign: string): string {
  return callSign ? callSign.split('-').pop() || '' : ''
}

/**
 * Group posts by type, deduplicating and collecting their times.
 * Mirrors FOM's Yg() helper.
 */
export interface GroupedPost {
  post: string
  isBreak: boolean
  times: { from: string; to: string }[]
}

export function groupPosts(posts: Post[]): GroupedPost[] {
  const map: Record<string, GroupedPost> = {}
  posts.forEach(p => {
    const key = (p.isBreak || p.post === 'Break') ? 'Break' : p.post
    if (!map[key]) {
      map[key] = {
        post: p.post,
        isBreak: p.isBreak || p.post === 'Break',
        times: [],
      }
    }
    if (p.from || p.to) map[key].times.push({ from: p.from, to: p.to })
  })
  return Object.values(map)
}

/**
 * Normalize call sign for matching — strips leading zeros from the
 * numeric portion so S-01 matches S-1.
 * Mirrors FOM's Tt() helper.
 */
export function normalizeCS(cs: string): string {
  if (!cs) return ''
  const m = cs.match(/^([A-Za-z]+-)(\d+)$/)
  return m ? m[1] + parseInt(m[2], 10) : cs.toUpperCase()
}

/**
 * Get secondary tasks assigned to a specific officer by call sign.
 * Merges multiple Fire Extinguisher entries into one combined row.
 * Mirrors FOM's Ma() + ls() pipeline.
 */
export function getTasksForCS(allTasks: SecondaryTask[], callSign: string): SecondaryTask[] {
  if (!callSign || !allTasks.length) return []
  const norm = normalizeCS(callSign)
  const mine = allTasks.filter(t => normalizeCS(t.callSign) === norm)
  if (!mine.length) return []

  const fe = mine.filter(t => t.task === 'Fire Extinguishers')
  const rest = mine.filter(t => t.task !== 'Fire Extinguishers')

  if (fe.length > 1) {
    const merged: SecondaryTask = {
      task: 'Fire Extinguishers',
      label: 'FE',
      detail: fe.map(f => (f.label || f.task) + (f.time ? ` @ ${f.time}` : '')).join(' / '),
      time: '',
      callSign: fe[0].callSign,
    }
    return [merged, ...rest]
  }
  if (fe.length === 1) return [fe[0], ...rest]
  return rest
}

/** Roster entry shape — what each row in the local roster looks like. */
export interface RosterEntry {
  name: string
  callSign: string
  badgeNumber?: number | null
  radioNumber?: number | null
  category?: string
}

/** Load the local-storage roster blob, defensively handling shape errors. */
export function loadRoster(): Record<string, RosterEntry[]> {
  try {
    const raw = localStorage.getItem('fom_roster')
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (typeof parsed === 'object' && !Array.isArray(parsed)) return parsed
    return {}
  } catch {
    return {}
  }
}

/**
 * Which roster categories are eligible to fill a given slot role.
 * E.g. a Sergeant slot can be filled by anyone Sergeant-rank or above.
 */
export function getRosterForRole(role: string): string[] {
  if (role === 'Dir/AD/Lt') {
    return ['Developer', 'Lieutenant', 'Director of Security', 'Assistant Director']
  }
  if (role === 'Sergeant') {
    return ['Developer', 'Lieutenant', 'Director of Security', 'Assistant Director', 'Sergeants']
  }
  if (role === 'OIC') {
    return ['Developer', 'Sergeants', 'Officers']
  }
  // Officer slots — exclude Engineering, Management, FCPD
  return ['Officers', 'Dispatchers']
}
